import React, { useState, useEffect } from "react";
import "./login.css";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Spinner } from "../../components";
import { API_URL } from "../../utils/baseUrl";
import { Toaster, encryptPassword, getRolePath } from "../../utils/common";
import { seedAuthSessionFromLogin } from "../../utils/authSession";
import {
  setTabHeartbeatNow,
  isWithinNewTabGrace,
  clearNewTabGrace,
} from "../../utils/tabHeartbeat";
import {
  clearAuthStorage,
  isSessionExpired,
  touchSessionActivity,
} from "../../utils/sessionAuth";
import { hydrateWorkTimeZoneFromUser } from "../../utils/workTimezonePreference";
import { USER_ACTION } from "../../redux/action";
import { Link } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import loginBanner from "../../images/LoginImg.png";
import LoginImg2 from "../../images/LoginImg2.png";
import LoginImg3 from "../../images/LoginImg3.png";
import { Facebook, X, Linkdin, Youtube, Instagram } from "../../images";
import { sendSMSOTP, verifySMSOTP } from "../../utils/smsOtpService";
import { sendEmailOTPFor2fa, verifyEmailOTP } from "../../utils/emailOtpService";
import {
  getOrCreateDeviceId,
  getTrustedBrowserCredentials,
  saveTrustedBrowserCredentials,
  TRUSTED_BROWSER_DAYS,
  resolveLoginOtpPhone,
  shouldUseEmailLoginOtp,
} from "../../utils/trustedBrowser";
import { clearPhone2faPromptSession } from "../../utils/loginPhone2fa";
import { maskPhoneNumber } from "../../utils/phoneUtils";

const maskEmailAddress = (email) => {
  const raw = String(email || "").trim();
  const at = raw.indexOf("@");
  if (at <= 1) return raw || "your email";
  const name = raw.slice(0, at);
  const domain = raw.slice(at);
  const visible = name.slice(0, Math.min(2, name.length));
  return `${visible}${"*".repeat(Math.max(name.length - visible.length, 1))}${domain}`;
};

const slides = [
  {
    image: loginBanner,
    link: "https://www.finlotax.com/",
    title: "Empowering startups with lasting financial peace of mind.",
    description:
      "Accurate bookkeeping, smart tax planning, and reliable CFO support all in one place.",
  },
  {
    image: LoginImg2,
    link: "https://www.finlotax.com/services/company-incorporation",
    title: "Build your business on the right foundation.",
    description:
      "Register your LLC, S Corporation, or C Corporation with confidence and get expert guidance every step of the way.",
  },
  {
    image: LoginImg3,
    link: "https://www.finlotax.com/services/tax-planning-and-advisor-services",
    title: "Plan smarter. Save more on taxes.",
    description:
      "Schedule a personalized tax planning session to uncover opportunities, minimize tax liability, and stay ahead year-round.",
  },
];

const LoginPage = () => {
  const [isLoaded, setLoader] = useState(false);
  const location = useLocation();
  const [password, setPassword] = useState("");

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 50;

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentIndex(
      (prevIndex) => (prevIndex - 1 + slides.length) % slides.length,
    );
  };

  const onTouchStart = (e) => {
    setIsHovered(true);
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    setIsHovered(false);
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  useEffect(() => {
    if (isHovered) return;

    const interval = setInterval(() => {
      nextSlide();
    }, 3000);

    return () => clearInterval(interval);
  }, [isHovered, currentIndex]);
  const [showPassword, setShowPassword] = useState(false); // State to toggle password visibility
  const [otp, setOtp] = useState(new Array(6).fill("")); // Phone SMS OTP
  const OTP_VALIDITY_SECONDS = 120; // 2 minutes
  const [resendTimer, setResendTimer] = useState(OTP_VALIDITY_SECONDS);
  const [otpSentAt, setOtpSentAt] = useState(null);
  const [userPhone, setUserPhone] = useState(null);
  const [otpChannel, setOtpChannel] = useState("sms"); // "sms" | "email"
  const [rememberBrowser, setRememberBrowser] = useState(false);
  const [pendingLoginUser, setPendingLoginUser] = useState(null);
  const [loginChallengeToken, setLoginChallengeToken] = useState(null);

  const query = new URLSearchParams(useLocation().search);
  useEffect(() => {
    if (isSessionExpired()) {
      clearAuthStorage();
    }
  }, []);

  useEffect(() => {
    if (query.get("googleAuth")) {
      setLoader(true);
      googleAuth(query.get("googleAuth"));
    }
  }, []);
  // const [values, setValues] = React.useState({
  //   showPassword: false,
  // });
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  const [inputs, setInputs] = useState({
    email: "",
    password: "",
    code: "",
  });
  const [emailError, setEmailError] = useState({
    state: false,
    msg: "",
  });
  const [error, setError] = useState({
    state: false,
    msg: "",
  });
  const [passwordError, setPasswordError] = useState({
    state: false,
    msg: "",
  });
  const [codeError, setCodeError] = useState({
    state: false,
    msg: "",
  });
  const [verifyOTPState, setVerifyOTP] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleChange = (e) => {
    setInputs((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    const { name, value } = e.target;
    if (name === "password") setPassword(value);
  };
  // const google = () => {
  //   window.open("http://localhost:4000/auth/google", "_self");
  // };

  function isValidEmail(email) {
    return /\S+@\S+\.\S+/.test(email);
  }

  const handleSubmit = async (e, verifyOTPState) => {
    e.preventDefault();
    try {
      if (!verifyOTPState) {
        if (inputs.email.length === 0) {
          setEmailError({
            name: "Please enter your email",
            state: true,
          });
        } else if (!isValidEmail(inputs.email)) {
          setEmailError({
            name: "Invalid Email",
            state: true,
          });
        } else {
          setEmailError({
            name: "",
            state: false,
          });
        }
        if (inputs.password.length === 0) {
          setPasswordError({
            name: "Please enter your password",
            state: true,
          });
        } else {
          setPasswordError({
            name: "",
            state: false,
          });
        }
        if (emailError.state === false && passwordError.state === false) {
          const processedPassword = encryptPassword(inputs.password);
          const { deviceId, token: trustedToken } =
            getTrustedBrowserCredentials();

          setLoader(true);
          const output = await axios.post(
            `${API_URL}auth/login`,
            {
              email: inputs.email,
              password: processedPassword,
              device_id: deviceId,
              ...(trustedToken
                ? { trusted_device_token: trustedToken }
                : {}),
            },
            { withCredentials: true },
          );
          setLoader(false);

          if (output.status === 200 && output.data.status === 1) {
            const loginUser =
              output.data.data?.[0] || output.data.data || {};
            const skipOtp =
              output.data.skip_otp === true ||
              output.data.otp_required === false ||
              loginUser.otp_required === false ||
              loginUser.skip_otp === true;

            // Trusted browser within 7 days → password-only login.
            // Keep the same stored device_id/token (do not rotate).
            if (
              skipOtp &&
              (loginUser.accessToken || output.data.data?.[0]?.accessToken)
            ) {
              Toaster.success(output.data.message || "Login successful");
              completeLogin(
                loginUser.accessToken ? loginUser : output.data.data[0],
              );
              return;
            }

            Toaster.success(output.data.message);

            const challengeToken =
              output.data.login_challenge_token ||
              loginUser.login_challenge_token ||
              null;

            setPendingLoginUser(loginUser);
            setLoginChallengeToken(challengeToken);
            setOtp(new Array(6).fill(""));
            setOtpSentAt(Date.now());
            setResendTimer(OTP_VALIDITY_SECONDS);
            setCodeError({ name: "", state: false });

            // No phone → explicitly send email OTP (login does not email it).
            // Phone for 2FA is collected after login in the app layout.
            const phoneNumber = resolveLoginOtpPhone(loginUser);
            if (!phoneNumber || shouldUseEmailLoginOtp(loginUser, output.data)) {
              setOtpChannel("email");
              setUserPhone(null);
              setLoader(true);
              const emailResult = await sendEmailOTPFor2fa(
                inputs.email,
                challengeToken,
              );
              setLoader(false);

              if (emailResult.success) {
                Toaster.success(emailResult.message || "OTP sent to your email");
                setVerifyOTP(true);
              } else {
                Toaster.error(
                  emailResult.message || "Failed to send email OTP",
                );
              }
              return;
            }

            setOtpChannel("sms");
            setUserPhone(phoneNumber);
            setLoader(true);
            const smsResult = await sendSMSOTP(phoneNumber, inputs.email);
            setLoader(false);

            if (
              smsResult.success &&
              (smsResult.smsFallback || smsResult.otpChannel === "email")
            ) {
              setOtpChannel("email");
              setUserPhone(null);
              setOtpSentAt(Date.now());
              setResendTimer(OTP_VALIDITY_SECONDS);
              setCodeError({ name: "", state: false });
              Toaster.success(
                smsResult.message || "OTP sent to your email",
              );
              setVerifyOTP(true);
            } else if (smsResult.success) {
              Toaster.success("SMS OTP sent to your phone number");
              setVerifyOTP(true);
            } else {
              // If SMS sending fails (Twilio outage, misconfig, invalid phone, etc.),
              // fall back to email OTP for 2FA.
              setLoader(true);
              const emailResult = await sendEmailOTPFor2fa(
                inputs.email,
                challengeToken,
              );
              setLoader(false);

              if (emailResult.success) {
                setOtpChannel("email");
                setUserPhone(null);
                setOtpSentAt(Date.now());
                setResendTimer(OTP_VALIDITY_SECONDS);
                setCodeError({ name: "", state: false });
                Toaster.success(
                  emailResult.message || "SMS failed. OTP sent to your email",
                );
                setVerifyOTP(true);
              } else {
                Toaster.error(
                  emailResult.message ||
                    smsResult.message ||
                    "Failed to send OTP",
                );
              }
            }
          } else {
            Toaster.error(output.data.message);
          }
        }
      } else {
        const finalOtp = otp.join("");
        const isEmailChannel = otpChannel === "email";

        if (finalOtp.length < 6) {
          setCodeError({
            name: isEmailChannel
              ? "Please enter complete email verification code"
              : "Please enter complete mobile verification code",
            state: true,
          });
          return;
        }
        if (!isEmailChannel && !userPhone) {
          setCodeError({
            name: "Phone number missing. Please go back and sign in again.",
            state: true,
          });
          return;
        }
        if (resendTimer === 0) {
          setCodeError({ name: "OTP expired", state: true });
          Toaster.warning("OTP expired. Please resend and try again.");
          return;
        }

        setLoader(true);
        const deviceId = getOrCreateDeviceId();
        const otpResult = isEmailChannel
          ? await verifyEmailOTP(finalOtp, inputs.email, {
              rememberBrowser,
              deviceId,
              trustedDays: TRUSTED_BROWSER_DAYS,
            })
          : await verifySMSOTP(finalOtp, userPhone, inputs.email, {
              rememberBrowser,
              deviceId,
              trustedDays: TRUSTED_BROWSER_DAYS,
            });
        setLoader(false);

        if (!otpResult.success) {
          setCodeError({
            name: otpResult.message || "Invalid OTP",
            state: true,
          });
          Toaster.error(otpResult.message || "Invalid OTP. Please try again");
          return;
        }

        setCodeError({ name: "", state: false });

        if (rememberBrowser && otpResult.trustedDeviceToken) {
          saveTrustedBrowserCredentials({
            token: otpResult.trustedDeviceToken,
            expiresAt: otpResult.trustedDeviceExpiresAt,
            deviceId: otpResult.deviceId || deviceId,
          });
        }

        const result = otpResult.user || pendingLoginUser;

        if (result?.accessToken) {
          Toaster.success("Login successful");
          completeLogin(result);
          return;
        }

        Toaster.error("Login failed. Please try again.");
      }
    } catch (error) {
      setLoader(false);
      if (error?.response?.status === 403) {
        Toaster.warning(
          error?.response?.data?.message ||
            "Access denied from this network. Contact your administrator.",
        );
        return;
      }
      Toaster.error("Error occurred while login. Please try again");
    }
  };

  const googleAuth = async (google_email_user_id) => {
    const signUpResponse = await USER_ACTION.googleSignUp(google_email_user_id);
    setLoader(false);

    if (signUpResponse.status === 200 && signUpResponse.data.status === 1) {
      const result = signUpResponse.data.data[0];
      localStorage.setItem("user_id", result?.client_id);
      localStorage.setItem("email", result.email);
      localStorage.setItem("first_name", result.first_name);
      localStorage.setItem("last_name", result.last_name);
      localStorage.setItem("user_role", "client");
      localStorage.setItem("employee_id", result.employee_id);
      localStorage.setItem("user_root_folder", result.s3_root_folder);
      const { accessToken: _ga, refreshToken: _gr, ...profileSafe } =
        result || {};
      localStorage.setItem("USER_DATA", JSON.stringify(profileSafe));
      seedAuthSessionFromLogin({ ...result, user_type: "client" });
      touchSessionActivity();
      clearPhone2faPromptSession();

      setTabHeartbeatNow();
      // Clients always choose Individual / Business first after login.
      navigate("/client/select-account", { replace: true });
    }
  };

  const handleBackToLogin = () => {
    setVerifyOTP(false);
    setOtp(new Array(6).fill(""));
    setResendTimer(OTP_VALIDITY_SECONDS);
    setOtpSentAt(null);
    setUserPhone(null);
    setOtpChannel("sms");
    setRememberBrowser(false);
    setPendingLoginUser(null);
    setLoginChallengeToken(null);
    setCodeError({ state: false, msg: "" });
  };

  // Complete login process
  const completeLogin = (result) => {
    const normalizedRole = String(result.user_type || "client").toLowerCase();

    localStorage.setItem(
      "user",
      JSON.stringify({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
      }),
    );

    localStorage.setItem(
      "user_id",
      normalizedRole === "client" ? result?.client_id : result?.employee_id,
    );

    localStorage.setItem("email", result.email);
    localStorage.setItem("first_name", result.first_name);
    localStorage.setItem("last_name", result.last_name);
    localStorage.setItem("user_role", normalizedRole);

    if (normalizedRole === "client") {
      localStorage.setItem("employee_id", result.employee_id);
      localStorage.setItem("user_root_folder", result?.s3_root_folder);
    }

    // Cache profile without embedding tokens (identity comes from JWT /auth/me).
    const { accessToken: _a, refreshToken: _r, ...profileSafe } = result || {};
    localStorage.setItem("USER_DATA", JSON.stringify(profileSafe));
    seedAuthSessionFromLogin(result);
    touchSessionActivity();
    hydrateWorkTimeZoneFromUser(result);
    clearPhone2faPromptSession();

    setTabHeartbeatNow();
    if (normalizedRole === "client") {
      // Clients always choose Individual / Business first after login.
      navigate("/client/select-account", { replace: true });
    } else {
      const rolePath = getRolePath("/dashboard");
      navigate(location.state?.from ? location.state.from : rolePath, {
        replace: true,
      });
    }
  };

  const handleOtpChange = (e, index) => {
    const value = e.target.value;
    if (isNaN(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`).focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`).focus();
    }
  };

  const handleOtpPaste = (e) => {
    const paste = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d+$/.test(paste)) return;
    const newOtp = paste.split("");
    setOtp(newOtp);
    document.getElementById(`otp-${newOtp.length - 1}`).focus();
  };

  const handleResendMobileOTP = async () => {
    if (resendTimer > 0) return;

    if (otpChannel === "email") {
      try {
        setLoader(true);
        const emailResult = await sendEmailOTPFor2fa(
          inputs.email,
          loginChallengeToken,
        );
        if (emailResult.success) {
          setResendTimer(OTP_VALIDITY_SECONDS);
          setOtpSentAt(Date.now());
          setOtp(new Array(6).fill(""));
          setCodeError({ name: "", state: false });
          Toaster.success(emailResult.message || "Email OTP sent again");
        } else {
          Toaster.error(emailResult.message || "Failed to resend OTP");
        }
      } catch (err) {
        Toaster.error(
          err?.response?.data?.message ||
            "Failed to resend OTP. Please try again.",
        );
      } finally {
        setLoader(false);
      }
      return;
    }

    if (!userPhone) {
      Toaster.error("Phone number missing. Please go back and sign in again.");
      return;
    }
    try {
      setLoader(true);
      const smsResult = await sendSMSOTP(userPhone, inputs.email);
      if (
        smsResult.success &&
        (smsResult.smsFallback || smsResult.otpChannel === "email")
      ) {
        setOtpChannel("email");
        setUserPhone(null);
        setResendTimer(OTP_VALIDITY_SECONDS);
        setOtpSentAt(Date.now());
        setOtp(new Array(6).fill(""));
        setCodeError({ name: "", state: false });
        Toaster.success(smsResult.message || "OTP sent to your email");
      } else if (smsResult.success) {
        setResendTimer(OTP_VALIDITY_SECONDS);
        setOtpSentAt(Date.now());
        setOtp(new Array(6).fill(""));
        setCodeError({ name: "", state: false });
        Toaster.success("SMS OTP sent again");
      } else {
        // If SMS resend fails, fall back to email OTP for 2FA.
        const emailResult = await sendEmailOTPFor2fa(
          inputs.email,
          loginChallengeToken,
        );

        if (emailResult.success) {
          setOtpChannel("email");
          setUserPhone(null);
          setResendTimer(OTP_VALIDITY_SECONDS);
          setOtpSentAt(Date.now());
          setOtp(new Array(6).fill(""));
          setCodeError({ name: "", state: false });
          Toaster.success(
            emailResult.message || "SMS failed. OTP sent to your email",
          );
        } else {
          Toaster.error(
            emailResult.message ||
              smsResult.message ||
              "Failed to resend OTP. Please try again.",
          );
        }
      }
    } catch (err) {
      Toaster.error(
        err?.response?.data?.message || "Failed to resend OTP. Please try again.",
      );
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (!verifyOTPState || resendTimer === 0) return;
    const interval = setInterval(() => {
      setResendTimer((t) => t - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [verifyOTPState, resendTimer]);

  useEffect(() => {
    let user = null;
    try {
      user = JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      user = null;
    }

    if (!user?.accessToken) return;

    if (isSessionExpired()) {
      clearAuthStorage();
      return;
    }

    setTabHeartbeatNow();
    if (isWithinNewTabGrace()) {
      clearNewTabGrace();
    }

    const from = location.state?.from;
    const currentPath = `${location.pathname}${location.search}`;
    const destination =
      typeof from === "string" && from.startsWith("/finlo")
        ? from
        : currentPath.startsWith("/finlo")
          ? currentPath
          : getRolePath("/dashboard");

    navigate(destination, { replace: true });
  }, [navigate, location.pathname, location.search, location.state]);

  return (
    <div className="login-page">
      <div className="login-wrapper">
        {/* LEFT CAROUSEL SECTION */}
        <div
          className="login-image"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`carousel-slide ${index === currentIndex ? "active" : ""}`}
            >
              <a
                href={slide.link}
                target="_blank"
                rel="noreferrer"
                className="carousel-slide-link"
                aria-label={`Visit FinloTax - ${slide.title}`}
              >
                <img src={slide.image} alt={`Login slide ${index + 1}`} />
              </a>
              <div className="image-overlay">
                <h2 className="hero-heading">
                  <span className="heading-line"></span>
                  {slide.title}
                </h2>
                <p>{slide.description}</p>
              </div>
            </div>
          ))}

          {/* Navigation Controls */}
          {/* <button
            type="button"
            className="carousel-control prev"
            onClick={prevSlide}
            aria-label="Previous slide"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          <button
            type="button"
            className="carousel-control next"
            onClick={nextSlide}
            aria-label="Next slide"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button> */}

          {/* Pagination Dots */}
          <div className="carousel-dots">
            {slides.map((_, index) => (
              <button
                key={index}
                type="button"
                className={`carousel-dot ${index === currentIndex ? "active" : ""}`}
                onClick={() => setCurrentIndex(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* RIGHT FORM SECTION */}
        <div className="login-form">
          <div className="form-header">
            <a href="https://finlotax.com/" target="__blank">
              <img src="/resources/images/Finlotax.png" alt="Finlotax Logo" />
            </a>

            <h3>
              {verifyOTPState
                ? otpChannel === "email"
                  ? "Verification code sent to your email"
                  : "Verification code sent to your phone"
                : "Welcome to Finlotax - Let's Get Started"}
            </h3>
          </div>
          <form>
            {/* EMAIL */}
            {!verifyOTPState && (
              <div className="form-group">
                <label style={{ marginTop: "32px", fontWeight: "bold" }}>
                  Email ID
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="example123@gmail.com"
                  onChange={handleChange}
                />
                {emailError.state && (
                  <span className="error-text">{emailError.name}</span>
                )}
              </div>
            )}

            {/* PASSWORD */}
            {!verifyOTPState && (
              <div className="form-group">
                <label style={{ fontWeight: "bold" }}>Password</label>
                <div className="password-box">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={handleChange}
                  />
                  <span onClick={togglePasswordVisibility}>
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
                {passwordError.state && (
                  <span className="error-text">{passwordError.name}</span>
                )}
              </div>
            )}
            {!verifyOTPState && (
              <div className="forgot-link">
                <Link to="/forget_password">Forgot Password?</Link>
              </div>
            )}
            {verifyOTPState && (
              <button
                type="button"
                className="forgot-link"
                onClick={handleBackToLogin}
                style={{ background: "none", border: "none", padding: 0 }}
              >
                ← Back To Login
              </button>
            )}
            {/* OTP — email or phone SMS */}
            {verifyOTPState && (
              <div>
                <div className="form-group">
                  <label>
                    {otpChannel === "email"
                      ? "Email Verification Code"
                      : "Mobile Verification Code"}
                  </label>
                  <p
                    style={{
                      fontSize: "0.85rem",
                      color: "#666",
                      marginBottom: "8px",
                    }}
                  >
                    {otpChannel === "email"
                      ? `Code sent to ${maskEmailAddress(inputs.email)}`
                      : `Code sent to ${maskPhoneNumber(userPhone) || "your phone"}`}
                  </p>
                  <div className="otp-container" onPaste={handleOtpPaste}>
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        id={`otp-${index}`}
                        type="text"
                        maxLength="1"
                        value={digit}
                        onChange={(e) => handleOtpChange(e, index)}
                        onKeyDown={(e) => handleOtpKeyDown(e, index)}
                        className="otp-input"
                      />
                    ))}
                  </div>
                  <div style={{ textAlign: "right", marginTop: "8px" }}>
                    {resendTimer > 0 ? (
                      <span
                        style={{
                          color: "#1c74bc",
                          fontSize: "0.85rem",
                          fontWeight: 600,
                        }}
                      >
                        Resend {otpChannel === "email" ? "Email" : "SMS"} OTP?{" "}
                        {Math.floor(resendTimer / 60)}:
                        {String(resendTimer % 60).padStart(2, "0")}
                      </span>
                    ) : (
                      <span
                        onClick={handleResendMobileOTP}
                        style={{
                          color: "#1c74bc",
                          fontSize: "0.85rem",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                      >
                        Resend OTP
                      </span>
                    )}
                  </div>

                  <label className="remember-browser-label">
                    <input
                      type="checkbox"
                      className="remember-browser-checkbox"
                      checked={rememberBrowser}
                      onChange={(e) => setRememberBrowser(e.target.checked)}
                    />
                    <span>
                      Remember this browser for {TRUSTED_BROWSER_DAYS} days
                    </span>
                  </label>
                  <p className="remember-browser-hint">
                    If checked, you won&apos;t need an OTP on this browser for
                    the next {TRUSTED_BROWSER_DAYS} days — password only.
                  </p>

                  {codeError.state && (
                    <span className="error-text">{codeError.name}</span>
                  )}
                </div>
              </div>
            )}

            <button
              className="submit-btn"
              onClick={(e) => handleSubmit(e, verifyOTPState)}
            >
              {verifyOTPState ? "Verify OTP" : "Sign In"}
            </button>
          </form>

          <p className="signup-text">
            Don&apos;t have an account? <Link to="/signup">Sign up</Link>
          </p>

          <p className="policy-links">
            <a href="https://finlotax.com/" target="_blank" rel="noreferrer">
              www.finlotax.com
            </a>{" "}
            |{" "}
            <a
              href ="https://finlotax.com/terms-and-conditions/"
              target="_blank"
              rel="noreferrer"
            >
              Term of Use
            </a>{" "}
            |{" "}
            <a href="https://finlotax.com/privacy-policy/" target="_blank" rel="noreferrer">
              Privacy Policy
            </a>{" "}
            |{" "}
            <a href="https://finlotax.com/disclaimer/" target="_blank" rel="noreferrer">
              Disclaimer
            </a>
          </p>

          <p className="media-links">
            <a href="https://www.facebook.com/finlotaxinc" target="_blank" rel="noreferrer">
              <img src={Facebook} alt="facebook" />
            </a>
            <a href="https://x.com/FinloTax" target="_blank" rel="noreferrer">
              <img src={X} alt="X" />
            </a>
            <a
              href="https://www.linkedin.com/company/finlotax-inc/"
              target="_blank"
              rel="noreferrer"
            >
              <img src={Linkdin} alt="Linkdin" />
            </a>
            <a href="https://www.instagram.com/finlotax_inc/" target="_blank" rel="noreferrer">
              <img src={Instagram} alt="Instagram" />
            </a>
            <a
              href="https://www.youtube.com/channel/UCjO5Yf5eGs1600kIZCgOT-Q"
              target="_blank"
              rel="noreferrer"
            >
              <img src={Youtube} alt="Youtube" />
            </a>
          </p>
        </div>
      </div>

      {isLoaded && <Spinner />}
    </div>
  );
};

export default LoginPage;
